import { Option } from 'Interfaces/FDPInterfaces/constrainedForecastBulkEdit';
import { msPlanBulkEditFilterInterface } from 'Interfaces/FDPInterfaces/tmsInterfaces';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { factScreenServices } from 'services/factsScreenServices';
import { tmsScreenServices } from 'services/tmsScreenServices';
import { deepCopy } from 'utils/deepCopy';

export const useFilterSection = ({
  isDataLoading,
  handleApplyFilter,
  parentFilterPayload,
  daveToSaveIsThere,
}: {
  isDataLoading: boolean;
  handleApplyFilter: (filterData: msPlanBulkEditFilterInterface) => void;
  parentFilterPayload: msPlanBulkEditFilterInterface;
  daveToSaveIsThere: boolean;
}) => {
  const [filterData, setFilterData] = useState<msPlanBulkEditFilterInterface>({
    Ramping: '',
    Concept: '',
    MoleculeAndDosageForm: '',
    StrategyCode: '',
    SellingCategory: '',
    GFC: '',
    ConceptSort: '', // Add missing ConceptSort property
    // COT: "",
  });

  const staticDropDownOptions = {
    RampingOptions: [
      { label: 'Yes', value: true },
      { label: 'No', value: false },
    ],
    StratCodeOptions: [
      { label: 'Model', value: 'model' },
      { label: 'Manual', value: 'manual' },
    ],
    SellingCategoryOptions: [
      { label: 'Direct', value: 'Direct' },
      { label: 'Indirect', value: 'Indirect' },
    ],
  };

  const [isAllMoleculesLoading, setIsAllMoleculesLoading] = useState<boolean>(true);
  const [isAllConceptLoading, setIsAllConceptLoading] = useState<boolean>(true);
  const [allMolecules, setAllMolecules] = useState<string[]>([]);
  const [allConcepts, setAllConcepts] = useState<string[]>([]);
  const [filteredMolecules, setFilteredMolecules] = useState<string[]>([]);
  const [filteredConcept, setFilteredConcept] = useState<string[]>([]);
  const [isFilterApplyButtonConfirmationVisible, setIsFilterApplyButtonConfirmationVisible] =
    useState<boolean>(false);

  const handlerFunctions = {
    handleFilterMolecules: async (moleculeDosageForm: string) => {
      const tempMolecules: string[] = [];

      for (const item of allMolecules) {
        const mol = item;
        if (mol.toLocaleLowerCase().search(moleculeDosageForm.toLocaleLowerCase()) !== -1) {
          tempMolecules.push(mol);
        }
      }

      setFilteredMolecules(tempMolecules);
    },

    handleFilterConcepts: async (concept: string) => {
      const tempConcepts: string[] = [];

      for (const item of allConcepts) {
        if (item.toLocaleLowerCase().search(concept.toLocaleLowerCase()) !== -1) {
          tempConcepts.push(item);
        }
      }

      setFilteredConcept(tempConcepts);
    },
  };
  const abortControllerRef = useRef<AbortController | null>(null);
  const getfilterOption = {
    getAllMolecules: async () => {
      try {
        setIsAllMoleculesLoading(true);
        const molecules = await factScreenServices.getAllMoleculeDetails();
        const temp = molecules.map(item => item.molecule_dose_form);
        setAllMolecules(temp);
      } catch (error) {
        console.log('Cannot fetch molecules: ', error);
        toast.error('Cannot fetch all the molecules');
      } finally {
        setIsAllMoleculesLoading(false);
      }
    },

    getAllConcepts: async () => {
      try {
        setIsAllConceptLoading(true);
        const concepts = await tmsScreenServices.getAllGSMSConceptForSalesPlan();
        const temp = concepts.map(item => item.concept);
        setAllConcepts(temp);
      } catch (error) {
        console.log('Cannot fetch all the concepts: ', error);
        toast.error('Cannot fetch all the concepts');
      } finally {
        setIsAllConceptLoading(false);
      }
    },

    fetchGFCSuggestions: async (inputValue: string): Promise<Option[]> => {
      // cancel any ongoing request if there is one
      if (abortControllerRef.current) {
        console.log('control coming here for the abort checker!!');
        abortControllerRef.current.abort(); // Abort the previous requrest
      }

      // Create a new Abort controller for the new request
      const controller = new AbortController();
      abortControllerRef.current = controller;

      return new Promise(resolve => {
        setTimeout(async () => {
          try {
            const response = await tmsScreenServices.getGFCSuggestionsForSalesPlan(
              inputValue,
              controller.signal
            );
            const suggestions: Option[] = [];

            for (const item of response) {
              suggestions.push({ label: item, value: item });
            }

            resolve(suggestions);
          } catch (error) {
            console.error('Error fetching the suggestions: ', error);
            resolve([]);
          }
        }, 500);
      });
    },
  };

  useEffect(() => {
    // getting all the molecules
    getfilterOption.getAllMolecules();
    getfilterOption.getAllConcepts();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run on mount
  }, []);

  const setFilterValues = (field: keyof msPlanBulkEditFilterInterface, value: string) => {
    const filterpayloadCopy = deepCopy(filterData);
    filterpayloadCopy[field] = value;
    setFilterData(filterpayloadCopy);
  };

  const handleFilter = async () => {
    if (daveToSaveIsThere) {
      setIsFilterApplyButtonConfirmationVisible(true);
    } else {
      handleApplyFilter(filterData);
    }
  };

  const isFilterButtonDisabled = () => {
    if (JSON.stringify(filterData) === JSON.stringify(parentFilterPayload) || isDataLoading) {
      return true;
    }
    return false;
  };

  const handleClickClearFilters = () => {
    setFilterData({
      Ramping: '',
      Concept: '',
      MoleculeAndDosageForm: '',
      StrategyCode: '',
      SellingCategory: '',
      GFC: '',
      ConceptSort: '', // Add missing ConceptSort property
      // COT: "",
    });
  };

  return {
    staticDropDownOptions,
    isAllMoleculesLoading,
    filterData,
    filteredMolecules,
    isAllConceptLoading,
    filteredConcept,
    setFilterValues,
    handleFilter,
    handlerFunctions,
    isFilterButtonDisabled,
    getfilterOption,
    isFilterApplyButtonConfirmationVisible,
    setIsFilterApplyButtonConfirmationVisible,
    handleClickClearFilters,
  };
};
